import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { subscribeToActiveAvailableQuests } from '../../services/questService';
import QuestDetailView from './QuestDetailView';
import { QuestLabel } from '../shared';
import FilterTabs from '../shared/FilterTabs';
import { stripMarkdown } from '../shared/MarkdownDisplay';
import { getLocalizedText } from '../../utils/localizationHelpers';
import { formatters } from '../../utils/dateUtils';
import { PageShell } from '../shared';

export default function QuestAccordion({
  section,
  availableQuests: availableQuestsProp,
  questHistory = [],
  questRequests = [],
  pendingRequests = [],
  approvedRequests = [],
  rejectedRequests = [],
  onStartQuest,
  hasActiveQuest = false
}) {
  const { t } = useTranslation();
  const [internalQuests, setInternalQuests] = useState([]);
  const [loading, setLoading] = useState(!availableQuestsProp);
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive log segment and filters from URL params
  const validSegments = ['requests', 'history'];
  const validRequestFilters = ['all', 'pending', 'approved', 'rejected'];
  const validHistoryFilters = ['all', 'completed', 'failed'];

  const logSegment = validSegments.includes(searchParams.get('segment')) ? searchParams.get('segment') : 'requests';
  const requestFilter = validRequestFilters.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all';
  const historyFilter = validHistoryFilters.includes(searchParams.get('filter')) ? searchParams.get('filter') : 'all';

  const updateParam = (key, value, defaults = {}) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      // Clear keys specified in defaults when they match default value
      if (defaults[key] === value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    });
  };
  const setLogSegment = (seg) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (seg === 'requests') { next.delete('segment'); } else { next.set('segment', seg); }
      next.delete('filter'); // reset filter on segment change
      return next;
    });
  };
  const setRequestFilter = (f) => updateParam('filter', f, { filter: 'all' });
  const setHistoryFilter = (f) => updateParam('filter', f, { filter: 'all' });

  useEffect(() => {
    if (availableQuestsProp) { setLoading(false); return; }
    const unsubscribe = subscribeToActiveAvailableQuests((quests) => {
      setInternalQuests(quests);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [availableQuestsProp]);

  const availableQuests = availableQuestsProp || internalQuests;

  const handleStartQuest = (questOrId) => {
    const questId = typeof questOrId === 'string' ? questOrId : questOrId?.id;
    if (hasActiveQuest || pendingRequests.length > 0 || !onStartQuest) return;
    const quest = availableQuests.find(q => q.id === questId);
    if (quest) {
      onStartQuest({
        id: quest.id,
        title: getLocalizedText(quest.title),
        description: getLocalizedText(quest.description),
        difficulty: quest.difficulty,
        timeEstimate: quest.timeEstimate,
        value: quest.value,
        type: quest.type || 'quest',
        sideQuests: quest.sideQuests || []
      });
    }
  };

  const selectedQuestId = searchParams.get('quest');
  const selectedQuest = selectedQuestId ? availableQuests.find(q => q.id === selectedQuestId) : null;

  const handleOpenDetail = (questId) => {
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('quest', questId); return next; });
  };
  const handleCloseDetail = () => {
    setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('quest'); return next; });
  };
  const handleDetailStartQuest = (questId) => { handleStartQuest(questId); handleCloseDetail(); };

  const filteredQuests = questHistory.filter(q => historyFilter === 'all' || q.status === historyFilter);
  const filteredRequests = questRequests.filter(r => requestFilter === 'all' || r.status === requestFilter);
  const canStartQuest = !hasActiveQuest && pendingRequests.length === 0;

  const requestTabs = useMemo(() => [
    { id: 'all', label: t('quests.history.all'), count: questRequests.length },
    { id: 'pending', label: t('quests.approval.pending'), count: pendingRequests.length },
    { id: 'approved', label: t('quests.approval.approved'), count: approvedRequests.length },
    { id: 'rejected', label: t('quests.approval.rejected'), count: rejectedRequests.length }
  ], [t, questRequests.length, pendingRequests.length, approvedRequests.length, rejectedRequests.length]);

  const historyTabs = useMemo(() => [
    { id: 'all', label: t('quests.history.all'), count: questHistory.length },
    { id: 'completed', label: t('quests.history.completed'), count: questHistory.filter(q => q.status === 'completed').length },
    { id: 'failed', label: t('quests.history.failed'), count: questHistory.filter(q => q.status === 'failed').length }
  ], [t, questHistory]);

  const getTotalXP = (quest) => {
    const mainXP = quest.value || quest.reward || 0;
    const sideQuestXP = (quest.sideQuests || []).reduce((total, sq) => total + (sq.value || 0), 0);
    return mainXP + sideQuestXP;
  };

  const formatDate = (dateString) => dateString ? formatters.shortDateTime(dateString) : '';

  // Section configs
  const sectionConfig = {
    available: { label: 'AVAILABLE QUESTS', icon: ICONS.MAP, count: availableQuests.length },
    questlog: { label: 'QUEST LOG', icon: ICONS.SCROLL },
    requests: { label: 'QUEST APPROVAL', icon: ICONS.HOURGLASS },
    history: { label: 'QUEST HISTORY', icon: ICONS.TROPHY }
  };

  const config = sectionConfig[section] || {};

  // If viewing a quest detail, render the detail view directly (no PageShell wrapper)
  if (section === 'available' && selectedQuest) {
    return (
      <QuestDetailView
        quest={selectedQuest}
        onBack={handleCloseDetail}
        onStartQuest={handleDetailStartQuest}
        hasActiveQuest={hasActiveQuest}
        pendingRequests={pendingRequests}
      />
    );
  }

  return (
    <PageShell className="qp-page">

      {/* Section Header */}
      <div className="qp-header">
        <div className="qp-section-label">
          <span className="qp-section-dot" />
          {config.label}
          {config.count > 0 && <span className="qp-header-count">{config.count}</span>}
        </div>
      </div>

      {section === 'available' && !selectedQuest && (
        <>
          {loading ? (
            <div className="qp-loading">
              <FontAwesomeIcon icon={ICONS.SPINNER} spin />
              <span>Loading quests...</span>
            </div>
          ) : availableQuests.length > 0 ? (
            <div className="qp-tile-grid">
              {availableQuests.map(quest => {
                const title = getLocalizedText(quest.title);
                const desc = getLocalizedText(quest.description);
                const totalXP = getTotalXP(quest);
                return (
                  <div
                    key={quest.id}
                    className="qp-quest-tile"
                    onClick={() => handleOpenDetail(quest.id)}
                  >
                    {quest.icon && <div className="qp-tile-bg" style={{ backgroundImage: `url(${quest.icon})` }} />}
                    <div className="qp-tile-gradient" />
                    <div className="qp-tile-content">
                      <div className="qp-tile-top">
                        <span className="qp-tile-xp">
                          <FontAwesomeIcon icon={ICONS.COINS} />
                          {totalXP} XP
                        </span>
                      </div>
                      <div className="qp-tile-bottom">
                        <h3 className="qp-tile-title">{title}</h3>
                        {desc && (() => {
                          const stripped = stripMarkdown(desc);
                          return <p className="qp-tile-desc">{stripped.substring(0, 100)}{stripped.length > 100 ? '...' : ''}</p>;
                        })()}
                        <div className="qp-tile-meta">
                          {quest.difficulty && (
                            <span className={`qp-pill qp-pill-diff ${quest.difficulty}`}>{quest.difficulty}</span>
                          )}
                          {quest.sideQuests?.length > 0 && (
                            <span className="qp-pill">{quest.sideQuests.length} Side Quests</span>
                          )}
                          {quest.timeEstimate && (
                            <span className="qp-pill">
                              <FontAwesomeIcon icon={ICONS.CLOCK} />
                              {quest.timeEstimate}
                            </span>
                          )}
                        </div>
                        {onStartQuest && (
                          <button
                            className="qp-tile-cta"
                            onClick={(e) => { e.stopPropagation(); handleStartQuest(quest.id); }}
                            disabled={!canStartQuest}
                            title={!canStartQuest ? (hasActiveQuest ? 'Quest active' : 'Pending approval') : ''}
                          >
                            <FontAwesomeIcon icon={ICONS.PLAY} />
                            <span>Request Quest</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="qp-empty">
              <div className="qp-empty-glow" />
              <div className="qp-empty-icon">
                <FontAwesomeIcon icon={ICONS.SCROLL} />
              </div>
              <h3>No quests available</h3>
              <p>Check back soon for new quests</p>
            </div>
          )}
        </>
      )}

      {/* Quest Requests — Filtered List */}
      {section === 'requests' && (
        <>
          <FilterTabs tabs={requestTabs} activeTab={requestFilter} onTabChange={setRequestFilter} />
          {filteredRequests.length > 0 ? (
            <div className="qp-card-list">
              {filteredRequests.map(request => {
                const questData = request.questData || {};
                const title = getLocalizedText(questData.title, 'Untitled Quest');
                const totalXP = getTotalXP(questData);
                return (
                  <div key={request.id} className={`qp-request-card ${request.status}`}>
                    <div className="qp-rcard-accent" data-status={request.status} />
                    {questData.icon && <div className="qp-rcard-bg" style={{ backgroundImage: `url(${questData.icon})` }} />}
                    <div className="qp-rcard-body">
                      <div className="qp-rcard-main">
                        <div className="qp-rcard-info">
                          <span className="qp-rcard-title">{title}</span>
                          <div className="qp-rcard-meta">
                            <QuestLabel type="xp" value={totalXP} size="small" />
                            {questData.sideQuests?.length > 0 && (
                              <QuestLabel type="sideQuests" value={questData.sideQuests.length} size="small" />
                            )}
                          </div>
                        </div>
                        <div className="qp-rcard-status-area">
                          <QuestLabel type="status" value={request.status} size="small" />
                          {request.requestedAt && (
                            <span className="qp-rcard-date">{formatDate(request.requestedAt)}</span>
                          )}
                        </div>
                      </div>
                      {request.approvalMessage && (
                        <div className="qp-rcard-message approved">
                          <FontAwesomeIcon icon={ICONS.INFO_CIRCLE} />
                          <span>{request.approvalMessage}</span>
                        </div>
                      )}
                      {request.rejectionMessage && (
                        <div className="qp-rcard-message rejected">
                          <FontAwesomeIcon icon={ICONS.EXCLAMATION} />
                          <span>{request.rejectionMessage}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="qp-empty">
              <div className="qp-empty-glow" />
              <div className="qp-empty-icon">
                <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
              </div>
              <h3>{t('quests.approval.noRequests', 'No quest requests')}</h3>
              <p>Your quest requests will appear here</p>
            </div>
          )}
        </>
      )}

      {/* Quest History — Filtered List */}
      {section === 'history' && (
        <>
          <FilterTabs tabs={historyTabs} activeTab={historyFilter} onTabChange={setHistoryFilter} />
          {filteredQuests.length > 0 ? (
            <div className="qp-card-list">
              {filteredQuests.map(quest => {
                const title = getLocalizedText(quest.title, 'Completed Quest');
                const totalXP = quest.reward || quest.value || 0;
                return (
                  <div key={quest.id} className={`qp-request-card ${quest.status}`}>
                    <div className="qp-rcard-accent" data-status={quest.status} />
                    {quest.icon && <div className="qp-rcard-bg" style={{ backgroundImage: `url(${quest.icon})` }} />}
                    <div className="qp-rcard-body">
                      <div className="qp-rcard-main">
                        <div className="qp-rcard-info">
                          <span className="qp-rcard-title">{title}</span>
                          <div className="qp-rcard-meta">
                            <QuestLabel type="xp" value={totalXP} size="small" />
                            {quest.sideQuests?.length > 0 && (
                              <QuestLabel type="sideQuests" value={quest.sideQuests.length} size="small" />
                            )}
                          </div>
                        </div>
                        <div className="qp-rcard-status-area">
                          <QuestLabel type="status" value={quest.status} size="small" />
                          {(quest.completedAt || quest.failedAt) && (
                            <span className="qp-rcard-date">{formatDate(quest.completedAt || quest.failedAt)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="qp-empty">
              <div className="qp-empty-glow" />
              <div className="qp-empty-icon">
                <FontAwesomeIcon icon={ICONS.TROPHY} />
              </div>
              <h3>{t('quests.history.noHistory')}</h3>
              <p>Complete quests to build your legacy</p>
            </div>
          )}
        </>
      )}

      {/* Quest Log — Merged Approvals + History */}
      {section === 'questlog' && (
        <>
          {/* Segment Toggle */}
          <div className="ql-segments">
            <button
              className={`ql-segment ${logSegment === 'requests' ? 'active' : ''}`}
              onClick={() => setLogSegment('requests')}
            >
              <FontAwesomeIcon icon={ICONS.HOURGLASS} />
              <span>Approvals</span>
              <span className="ql-segment-count">{questRequests.length}</span>
            </button>
            <button
              className={`ql-segment ${logSegment === 'history' ? 'active' : ''}`}
              onClick={() => setLogSegment('history')}
            >
              <FontAwesomeIcon icon={ICONS.TROPHY} />
              <span>History</span>
              <span className="ql-segment-count">{questHistory.length}</span>
            </button>
          </div>

          {/* Sub-filters */}
          {logSegment === 'requests' && (
            <FilterTabs tabs={requestTabs} activeTab={requestFilter} onTabChange={setRequestFilter} />
          )}
          {logSegment === 'history' && (
            <FilterTabs tabs={historyTabs} activeTab={historyFilter} onTabChange={setHistoryFilter} />
          )}

          {/* Requests cards */}
          {logSegment === 'requests' && (
            filteredRequests.length > 0 ? (
              <div className="qp-card-list">
                {filteredRequests.map(request => {
                  const questData = request.questData || {};
                  const title = getLocalizedText(questData.title, 'Untitled Quest');
                  const totalXP = getTotalXP(questData);
                  return (
                    <div key={request.id} className={`qp-request-card ${request.status}`}>
                      <div className="qp-rcard-accent" data-status={request.status} />
                      {questData.icon && <div className="qp-rcard-bg" style={{ backgroundImage: `url(${questData.icon})` }} />}
                      <div className="qp-rcard-body">
                        <div className="qp-rcard-main">
                          <div className="qp-rcard-info">
                            <span className="qp-rcard-title">{title}</span>
                            <div className="qp-rcard-meta">
                              <QuestLabel type="xp" value={totalXP} size="small" />
                              {questData.sideQuests?.length > 0 && (
                                <QuestLabel type="sideQuests" value={questData.sideQuests.length} size="small" />
                              )}
                            </div>
                          </div>
                          <div className="qp-rcard-status-area">
                            <QuestLabel type="status" value={request.status} size="small" />
                            {request.requestedAt && (
                              <span className="qp-rcard-date">{formatDate(request.requestedAt)}</span>
                            )}
                          </div>
                        </div>
                        {request.approvalMessage && (
                          <div className="qp-rcard-message approved">
                            <FontAwesomeIcon icon={ICONS.INFO_CIRCLE} />
                            <span>{request.approvalMessage}</span>
                          </div>
                        )}
                        {request.rejectionMessage && (
                          <div className="qp-rcard-message rejected">
                            <FontAwesomeIcon icon={ICONS.EXCLAMATION} />
                            <span>{request.rejectionMessage}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="qp-empty">
                <div className="qp-empty-glow" />
                <div className="qp-empty-icon">
                  <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
                </div>
                <h3>{t('quests.approval.noRequests', 'No quest requests')}</h3>
                <p>Your quest requests will appear here</p>
              </div>
            )
          )}

          {/* History cards */}
          {logSegment === 'history' && (
            filteredQuests.length > 0 ? (
              <div className="qp-card-list">
                {filteredQuests.map(quest => {
                  const title = getLocalizedText(quest.title, 'Completed Quest');
                  const totalXP = quest.reward || quest.value || 0;
                  return (
                    <div key={quest.id} className={`qp-request-card ${quest.status}`}>
                      <div className="qp-rcard-accent" data-status={quest.status} />
                      {quest.icon && <div className="qp-rcard-bg" style={{ backgroundImage: `url(${quest.icon})` }} />}
                      <div className="qp-rcard-body">
                        <div className="qp-rcard-main">
                          <div className="qp-rcard-info">
                            <span className="qp-rcard-title">{title}</span>
                            <div className="qp-rcard-meta">
                              <QuestLabel type="xp" value={totalXP} size="small" />
                              {quest.sideQuests?.length > 0 && (
                                <QuestLabel type="sideQuests" value={quest.sideQuests.length} size="small" />
                              )}
                            </div>
                          </div>
                          <div className="qp-rcard-status-area">
                            <QuestLabel type="status" value={quest.status} size="small" />
                            {(quest.completedAt || quest.failedAt) && (
                              <span className="qp-rcard-date">{formatDate(quest.completedAt || quest.failedAt)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="qp-empty">
                <div className="qp-empty-glow" />
                <div className="qp-empty-icon">
                  <FontAwesomeIcon icon={ICONS.TROPHY} />
                </div>
                <h3>{t('quests.history.noHistory')}</h3>
                <p>Complete quests to build your legacy</p>
              </div>
            )
          )}
        </>
      )}
    </PageShell>
  );
}
